import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Font-face definition
const fontFaceStyle = `
  @font-face {
    font-family: 'Apple Cards Afsal';
    src: url('/fonts/anek.ttf') format('truetype');
  }
`;

// Helper: bold check
const isBold = (word) =>
  typeof word === "object" && Object.hasOwn(word, "bold");

// Currency symbol helper
const getCurrencySymbol = (currency) => {
  switch (currency) {
    case "SAR":
      return "SR";
    case "QAR":
      return "QR";
    case "AED":
      return "AED";
    case "MYR":
      return "RM";
    case "INR":
    default:
      return "₹";
  }
};

export function CertificateModal({ isOpen, onClose, sponsorship }) {
  const certificateRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [posterImage, setPosterImage] = useState(null);
  const [fontLoaded, setFontLoaded] = useState(false);
  const [language, setLanguage] = useState("malayalam");

  // Load background image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setPosterImage(img);
    img.onerror = (e) => console.error("Error loading image:", e);
    img.src = "/sponsor-2026.jpeg";
  }, []);

  const loadCustomFont = async () => {
    const customFont = new FontFace(
      "Apple Cards Afsal",
      "url(/fonts/anek.ttf)",
    );
    try {
      const loadedFont = await customFont.load();
      document.fonts.add(loadedFont);
      setFontLoaded(true);
    } catch (error) {
      console.error("Failed to load font:", error);
    }
  };

  useEffect(() => {
    loadCustomFont();
  }, []);

  // ---------- Malayalam Certificate ----------
  const generateCertificate = async () => {
    if (!posterImage || !fontLoaded) {
      alert("Resources still loading. Please try again in a moment.");
      return null;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 783 * 2;
    canvas.height = 1000 * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.drawImage(posterImage, 0, 0, 783, 1000);

    ctx.textBaseline = "top";
    ctx.fillStyle = "#1f2937";
    ctx.textAlign = "left";

    const contentWidth = 580;
    const startX = 100;
    const startY = 460;
    const lineHeight = 22;
    const indentSize = 40;

    // Helper: draw justified line
    const drawJustifiedLine = (words, x, y, maxWidth, isLastLine = false) => {
      if (isLastLine) {
        let currentX = x;
        for (let i = 0; i < words.length; i++) {
          if (isBold(words[i])) {
            ctx.font = words[i].bold
              ? 'bold 16px "Apple Cards Afsal"'
              : '16px "Apple Cards Afsal"';
            ctx.fillText(words[i].text, currentX, y);
            currentX += ctx.measureText(words[i].text).width + 10;
          } else {
            ctx.font = '16px "Apple Cards Afsal"';
            ctx.fillText(words[i], currentX, y);
            currentX += ctx.measureText(words[i]).width + 10;
          }
        }
        return;
      }

      let wordsWidth = 0;
      for (let word of words) {
        if (isBold(word)) {
          ctx.font = word.bold
            ? 'bold 16px "Apple Cards Afsal"'
            : '16px "Apple Cards Afsal"';
          wordsWidth += ctx.measureText(word.text).width;
        } else {
          ctx.font = '16px "Apple Cards Afsal"';
          wordsWidth += ctx.measureText(word).width;
        }
      }

      const totalSpacing = maxWidth - wordsWidth;
      const spaceBetweenWords =
        words.length > 1 ? totalSpacing / (words.length - 1) : 0;

      let currentX = x;
      for (let i = 0; i < words.length; i++) {
        if (isBold(words[i])) {
          ctx.font = words[i].bold
            ? 'bold 16px "Apple Cards Afsal"'
            : '16px "Apple Cards Afsal"';
          ctx.fillText(words[i].text, currentX, y);
          currentX += ctx.measureText(words[i].text).width + spaceBetweenWords;
        } else {
          ctx.font = '16px "Apple Cards Afsal"';
          ctx.fillText(words[i], currentX, y);
          currentX += ctx.measureText(words[i]).width + spaceBetweenWords;
        }
      }
    };

    // Greeting
    ctx.font = '16px "Apple Cards Afsal"';
    ctx.fillText("അസ്സലാമു അലൈക്കും", startX, startY);

    // Salutation
    let currentY = startY + lineHeight;
    let currentX = startX;
    ctx.font = '16px "Apple Cards Afsal"';
    ctx.fillText("പ്രിയപ്പെട്ട ", startX, currentY);
    currentX += ctx.measureText("പ്രിയപ്പെട്ട ").width;
    ctx.font = 'bold 16px "Apple Cards Afsal"';
    ctx.fillText(sponsorship.sponsorName, currentX, currentY);
    currentX += ctx.measureText(sponsorship.sponsorName).width;
    ctx.font = '16px "Apple Cards Afsal"';
    ctx.fillText(",", currentX, currentY);

    // First paragraph – uses amountPromised
    currentY = startY + lineHeight * 3;

    const currSymbol = getCurrencySymbol(sponsorship.currency);
    const amountText = `${currSymbol} ${sponsorship.amountPromised}/- ${
      sponsorship.currency === "INR" ? "രൂപ" : ""
    }`;

    const firstParaSegments = [
      { text: "ഹാദിയ ദേശീയ വിദ്യാഭ്യാസ പദ്ധതിയുടെ ഭാഗമായി ", bold: false },
      { text: amountText, bold: true },
      {
        text: " സംഭാവന നല്‍കാന്‍ താങ്കള്‍ സന്നദ്ധതയറിയിച്ചതിന് നന്ദി.",
        bold: false,
      },
    ];

    let allWords = [];
    for (let segment of firstParaSegments) {
      const words = segment.text.split(" ");
      for (let word of words) {
        allWords.push({ text: word, bold: segment.bold });
      }
    }

    let line = [];
    let lineWidth = 0;
    let isFirstLine = true;
    ctx.font = '16px "Apple Cards Afsal"';

    for (let i = 0; i < allWords.length; i++) {
      ctx.font = allWords[i].bold
        ? 'bold 16px "Apple Cards Afsal"'
        : '16px "Apple Cards Afsal"';
      const wordWidth = ctx.measureText(allWords[i].text).width;
      const spaceWidth = ctx.measureText(" ").width;

      if (
        lineWidth + wordWidth + (line.length > 0 ? spaceWidth : 0) >
        contentWidth - (isFirstLine ? indentSize : 0)
      ) {
        drawJustifiedLine(
          line,
          startX + (isFirstLine ? indentSize : 0),
          currentY,
          contentWidth - (isFirstLine ? indentSize : 0),
          false,
        );
        currentY += lineHeight;
        line = [allWords[i]];
        lineWidth = wordWidth;
        isFirstLine = false;
      } else {
        line.push(allWords[i]);
        lineWidth += wordWidth + (line.length > 1 ? spaceWidth : 0);
      }
    }

    if (line.length > 0) {
      let cx = startX + (isFirstLine ? indentSize : 0);
      for (let item of line) {
        ctx.font = item.bold
          ? 'bold 16px "Apple Cards Afsal"'
          : '16px "Apple Cards Afsal"';
        ctx.fillText(item.text, cx, currentY);
        cx += ctx.measureText(item.text).width + 10;
      }
      currentY += lineHeight;
    }

    currentY += lineHeight * 0.3;

    // Second paragraph (phone number)
    const secondParaSegments = [
      { text: "സംഭാവനാ തുക നല്‍കിയതിനു ശേഷം ", bold: false },
      { text: "+919744477555", bold: true },
      {
        text: " നമ്പറില്‍ വിവരമറിയിച്ച് റസിപ്റ്റ് കൈപ്പറ്റുമല്ലോ. നന്മയുടെ വഴിയിലുള്ള ഈ സഹകരണത്തിന് നാഥന്‍ അര്‍ഹമായ പ്രതിഫലം നല്‍കട്ടെ. ആമീന്‍",
        bold: false,
      },
    ];

    allWords = [];
    for (let segment of secondParaSegments) {
      const words = segment.text.split(" ");
      for (let word of words) {
        allWords.push({ text: word, bold: segment.bold });
      }
    }

    isFirstLine = true;
    line = [];
    lineWidth = 0;

    for (let i = 0; i < allWords.length; i++) {
      ctx.font = allWords[i].bold
        ? 'bold 16px "Apple Cards Afsal"'
        : '16px "Apple Cards Afsal"';
      const wordWidth = ctx.measureText(allWords[i].text).width;
      const spaceWidth = ctx.measureText(" ").width;

      if (
        lineWidth + wordWidth + (line.length > 0 ? spaceWidth : 0) >
        contentWidth - (isFirstLine ? indentSize : 0)
      ) {
        let cx = startX + (isFirstLine ? indentSize : 0);
        if (line.length === 1) {
          ctx.font = line[0].bold
            ? 'bold 16px "Apple Cards Afsal"'
            : '16px "Apple Cards Afsal"';
          ctx.fillText(line[0].text, cx, currentY);
        } else {
          const totalSpace =
            contentWidth -
            (isFirstLine ? indentSize : 0) -
            lineWidth +
            (line.length - 1) * spaceWidth;
          const spacing = totalSpace / (line.length - 1);
          for (let j = 0; j < line.length; j++) {
            ctx.font = line[j].bold
              ? 'bold 16px "Apple Cards Afsal"'
              : '16px "Apple Cards Afsal"';
            ctx.fillText(line[j].text, cx, currentY);
            if (j < line.length - 1) {
              cx += ctx.measureText(line[j].text).width + spacing;
            }
          }
        }
        currentY += lineHeight;
        line = [allWords[i]];
        lineWidth = wordWidth;
        isFirstLine = false;
      } else {
        line.push(allWords[i]);
        lineWidth += wordWidth + (line.length > 1 ? spaceWidth : 0);
      }
    }

    if (line.length > 0) {
      let cx = startX + (isFirstLine ? indentSize : 0);
      for (let item of line) {
        ctx.font = item.bold
          ? 'bold 16px "Apple Cards Afsal"'
          : '16px "Apple Cards Afsal"';
        ctx.fillText(item.text, cx, currentY);
        cx += ctx.measureText(item.text).width + 10;
      }
    }

    // Signature
    ctx.textAlign = "center";
    ctx.font = '16px "Apple Cards Afsal"';
    ctx.fillText("ജനറൽ സെക്രട്ടറി", 600, 710);
    ctx.fillText("ഹാദിയ", 600, 710 + lineHeight);

    // Sponsorship ID
    ctx.save();
    ctx.translate(40, 900);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "left";
    ctx.font = '12px "Arial"';
    ctx.fillStyle = "#666666";
    ctx.fillText(`#${sponsorship.sponsorshipId}`, 0, 0);
    ctx.restore();

    return canvas.toDataURL("image/png");
  };

  // ---------- English Certificate ----------
  const generateEnglishCertificate = async () => {
    if (!posterImage || !fontLoaded) {
      alert("Resources still loading. Please try again in a moment.");
      return null;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 783 * 2;
    canvas.height = 1000 * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.drawImage(posterImage, 0, 0, 783, 1000);

    ctx.textBaseline = "top";
    ctx.fillStyle = "#1f2937";
    ctx.textAlign = "left";

    const contentWidth = 560;
    const startX = 100;
    const startY = 460;
    const lineHeight = 26;
    const indentSize = 40;
    const baseFontSize = 18;
    const signatureFontSize = 16;

    const drawJustifiedLine = (words, x, y, maxWidth, isLastLine = false) => {
      if (isLastLine) {
        let currentX = x;
        for (let i = 0; i < words.length; i++) {
          if (isBold(words[i])) {
            ctx.font = words[i].bold
              ? `bold ${baseFontSize}px "Apple Cards Afsal"`
              : `${baseFontSize}px "Apple Cards Afsal"`;
            ctx.fillText(words[i].text, currentX, y);
            currentX += ctx.measureText(words[i].text).width + 8;
          } else {
            ctx.font = `${baseFontSize}px "Apple Cards Afsal"`;
            ctx.fillText(words[i], currentX, y);
            currentX += ctx.measureText(words[i]).width + 8;
          }
        }
        return;
      }

      let wordsWidth = 0;
      for (let word of words) {
        if (isBold(word)) {
          ctx.font = word.bold
            ? `bold ${baseFontSize}px "Apple Cards Afsal"`
            : `${baseFontSize}px "Apple Cards Afsal"`;
          wordsWidth += ctx.measureText(word.text).width;
        } else {
          ctx.font = `${baseFontSize}px "Apple Cards Afsal"`;
          wordsWidth += ctx.measureText(word).width;
        }
      }

      const totalSpacing = maxWidth - wordsWidth;
      const spaceBetweenWords =
        words.length > 1 ? totalSpacing / (words.length - 1) : 0;

      let currentX = x;
      for (let i = 0; i < words.length; i++) {
        if (isBold(words[i])) {
          ctx.font = words[i].bold
            ? `bold ${baseFontSize}px "Apple Cards Afsal"`
            : `${baseFontSize}px "Apple Cards Afsal"`;
          ctx.fillText(words[i].text, currentX, y);
        } else {
          ctx.font = `${baseFontSize}px "Apple Cards Afsal"`;
          ctx.fillText(words[i], currentX, y);
        }
        currentX += ctx.measureText(
          isBold(words[i]) ? words[i].text : words[i],
        ).width;
        if (i < words.length - 1) currentX += spaceBetweenWords;
      }
    };

    // Greeting
    ctx.font = `${baseFontSize}px "Apple Cards Afsal"`;
    ctx.fillText("Assalamu Alaikum", startX, startY);

    // Salutation
    let currentY = startY + lineHeight;
    let currentX = startX;
    ctx.font = `${baseFontSize}px "Apple Cards Afsal"`;
    ctx.fillText("Dear ", startX, currentY);
    currentX += ctx.measureText("Dear ").width;
    ctx.font = `bold ${baseFontSize}px "Apple Cards Afsal"`;
    ctx.fillText(sponsorship.sponsorName, currentX, currentY);
    currentX += ctx.measureText(sponsorship.sponsorName).width;
    ctx.font = `${baseFontSize}px "Apple Cards Afsal"`;
    ctx.fillText(",", currentX, currentY);

    // First paragraph – uses amountPromised
    currentY = startY + lineHeight * 2.5;

    const currSymbol = getCurrencySymbol(sponsorship.currency);

    const firstParaSegments = [
      {
        text: "Thank you for your willingness to contribute ",
        bold: false,
      },
      {
        text: `${currSymbol} ${sponsorship.amountPromised}/-`,
        bold: true,
      },
      {
        text: " as part of the Hadia National Education Program.",
        bold: false,
      },
    ];

    let allWords = [];
    for (let segment of firstParaSegments) {
      const words = segment.text.split(" ");
      for (let word of words) {
        allWords.push({ text: word, bold: segment.bold });
      }
    }

    let line = [];
    let lineWidth = 0;
    let isFirstLine = true;

    for (let i = 0; i < allWords.length; i++) {
      ctx.font = allWords[i].bold
        ? `bold ${baseFontSize}px "Apple Cards Afsal"`
        : `${baseFontSize}px "Apple Cards Afsal"`;
      const wordWidth = ctx.measureText(allWords[i].text).width;
      const spaceWidth = ctx.measureText(" ").width;

      if (
        lineWidth + wordWidth + (line.length > 0 ? spaceWidth : 0) >
        contentWidth - (isFirstLine ? indentSize : 0)
      ) {
        drawJustifiedLine(
          line,
          startX + (isFirstLine ? indentSize : 0),
          currentY,
          contentWidth - (isFirstLine ? indentSize : 0),
          false,
        );
        currentY += lineHeight;
        line = [allWords[i]];
        lineWidth = wordWidth;
        isFirstLine = false;
      } else {
        line.push(allWords[i]);
        lineWidth += wordWidth + (line.length > 1 ? spaceWidth : 0);
      }
    }

    if (line.length > 0) {
      drawJustifiedLine(
        line,
        startX + (isFirstLine ? indentSize : 0),
        currentY,
        contentWidth - (isFirstLine ? indentSize : 0),
        true,
      );
      currentY += lineHeight;
    }

    currentY += lineHeight * 0.2;

    // Second paragraph
    const secondParaSegments = [
      { text: "After making the contribution, please contact ", bold: false },
      { text: "+919744477555", bold: true },
      {
        text: " to receive your receipt. May Allah reward you with the deserved reward for this cooperation in the path of goodness. Ameen.",
        bold: false,
      },
    ];

    allWords = [];
    for (let segment of secondParaSegments) {
      const words = segment.text.split(" ");
      for (let word of words) {
        allWords.push({ text: word, bold: segment.bold });
      }
    }

    line = [];
    lineWidth = 0;
    isFirstLine = true;

    for (let i = 0; i < allWords.length; i++) {
      ctx.font = allWords[i].bold
        ? `bold ${baseFontSize}px "Apple Cards Afsal"`
        : `${baseFontSize}px "Apple Cards Afsal"`;
      const wordWidth = ctx.measureText(allWords[i].text).width;
      const spaceWidth = ctx.measureText(" ").width;

      if (
        lineWidth + wordWidth + (line.length > 0 ? spaceWidth : 0) >
        contentWidth - (isFirstLine ? indentSize : 0)
      ) {
        drawJustifiedLine(
          line,
          startX + (isFirstLine ? indentSize : 0),
          currentY,
          contentWidth - (isFirstLine ? indentSize : 0),
          false,
        );
        currentY += lineHeight;
        line = [allWords[i]];
        lineWidth = wordWidth;
        isFirstLine = false;
      } else {
        line.push(allWords[i]);
        lineWidth += wordWidth + (line.length > 1 ? spaceWidth : 0);
      }
    }

    if (line.length > 0) {
      drawJustifiedLine(
        line,
        startX + (isFirstLine ? indentSize : 0),
        currentY,
        contentWidth - (isFirstLine ? indentSize : 0),
        true,
      );
    }

    // Signature
    ctx.textAlign = "center";
    ctx.font = `${signatureFontSize}px "Apple Cards Afsal"`;
    ctx.fillText("General Secretary", 580, 710);
    ctx.fillText("HADIA", 580, 710 + lineHeight);

    // Sponsorship ID
    ctx.save();
    ctx.translate(40, 900);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "left";
    ctx.font = '12px "Arial"';
    ctx.fillStyle = "#666666";
    ctx.fillText(`#${sponsorship.sponsorshipId}`, 0, 0);
    ctx.restore();

    return canvas.toDataURL("image/png");
  };

  // ---------- Download handler ----------
  const handleDownload = async () => {
    try {
      setIsLoading(true);
      await document.fonts.ready;

      const dataUrl =
        language === "malayalam"
          ? await generateCertificate()
          : await generateEnglishCertificate();

      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

      if (isIOS) {
        const newTab = window.open();
        if (newTab) {
          newTab.document.write(`
            <html>
              <head>
                <title>Letter - ${sponsorship.sponsorName}</title>
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                  body { margin: 0; padding: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: -apple-system, BlinkMacSystemFont, sans-serif; background-color: #f5f5f5; text-align: center; }
                  img { max-width: 100%; max-height: 85vh; box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
                  .instructions { margin-top: 20px; padding: 10px; background-color: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); max-width: 90%; }
                </style>
              </head>
              <body>
                <img src="${dataUrl}" alt="Letter"/>
                <div class="instructions"><p>Press and hold on the image, then select "Copy" or "Share"</p></div>
              </body>
            </html>
          `);
          newTab.document.close();
        } else {
          alert(
            'Please long-press on the letter preview and select "Copy" or "Share"',
          );
        }
      } else {
        const link = document.createElement("a");
        link.download = `sponsorship-letter-${sponsorship.sponsorshipId}.png`;
        link.href = dataUrl;
        link.click();
      }
    } catch (error) {
      console.error("Error generating letter:", error);
      alert("An error occurred while generating the letter. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const currSymbol = getCurrencySymbol(sponsorship.currency);

  return (
    <>
      <style>{fontFaceStyle}</style>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className="sm:max-w-[95vw] max-h-[95vh] overflow-y-auto p-4 md:p-6"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <Tabs defaultValue="malayalam" onValueChange={setLanguage}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="malayalam">Malayalam</TabsTrigger>
              <TabsTrigger value="english">English</TabsTrigger>
            </TabsList>

            {/* -------- Malayalam Tab -------- */}
            <TabsContent value="malayalam">
              {/* Mobile View */}
              <div className="block sm:hidden">
                <div className="flex flex-col items-center space-y-4">
                  <div className="text-center space-y-2">
                    <h3 className="font-semibold text-lg">
                      Sponsorship Letter
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Download Letter for {currSymbol}
                      {sponsorship.amountPromised.toLocaleString()} sponsorship
                    </p>
                  </div>
                  <div className="w-full max-w-sm space-y-4">
                    <div className="bg-muted p-4 rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Sponsor Name
                        </span>
                        <span className="text-sm font-medium">
                          {sponsorship.sponsorName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Amount Promised
                        </span>
                        <span className="text-sm font-medium">
                          {currSymbol}
                          {sponsorship.amountPromised.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop View */}
              <div className="hidden sm:block">
                <div className="flex flex-col items-center space-y-4 w-full">
                  <div
                    className="relative w-[783px] h-[1000px]"
                    style={{ maxWidth: "100%", aspectRatio: "783/1000" }}
                  >
                    <div
                      ref={certificateRef}
                      className="absolute inset-0 bg-white"
                      style={{
                        width: "783px",
                        height: "1000px",
                        position: "relative",
                      }}
                    >
                      {posterImage && (
                        <img
                          src="/sponsor-2026.jpeg"
                          alt="Certificate background"
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}

                      <div
                        className="absolute text-gray-800"
                        style={{
                          top: "55%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "550px",
                          fontSize: "16px",
                          lineHeight: "1",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          zIndex: 1,
                        }}
                      >
                        <div>അസ്സലാമു അലൈക്കും</div>
                        <br />
                        <div>
                          പ്രിയപ്പെട്ട{" "}
                          <span style={{ fontWeight: "bold" }}>
                            {sponsorship.sponsorName}
                          </span>
                          ,
                        </div>
                        <br />
                        <div
                          className="text-justify"
                          style={{ lineHeight: "1.6" }}
                        >
                          &nbsp;&nbsp;&nbsp;ഹാദിയ ദേശീയ വിദ്യാഭ്യാസ പദ്ധതിയുടെ
                          ഭാഗമായി{" "}
                          <span style={{ fontWeight: "bold" }}>
                            {currSymbol} {sponsorship.amountPromised}/-{" "}
                            {sponsorship.currency === "INR" ? "രൂപ" : ""}
                          </span>{" "}
                          സംഭാവന നല്‍കാന്‍ താങ്കള്‍ സന്നദ്ധതയറിയിച്ചതിന് നന്ദി.
                          <br />
                          &nbsp;&nbsp;&nbsp;സംഭാവനാ തുക നല്‍കിയതിനു ശേഷം{" "}
                          <span style={{ fontWeight: "bold" }}>
                            +919744477555
                          </span>{" "}
                          നമ്പറില്‍ വിവരമറിയിച്ച് റസിപ്റ്റ് കൈപ്പറ്റുമല്ലോ.
                          നന്മയുടെ വഴിയിലുള്ള ഈ സഹകരണത്തിന് നാഥന്‍ അര്‍ഹമായ
                          പ്രതിഫലം നല്‍കട്ടെ. ആമീന്‍
                        </div>
                      </div>

                      <div
                        style={{
                          position: "absolute",
                          bottom: "100px",
                          left: "40px",
                          transform: "rotate(-90deg)",
                          transformOrigin: "left bottom",
                          fontSize: "12px",
                          color: "#666666",
                          fontFamily: "Arial",
                        }}
                      >
                        #{sponsorship.sponsorshipId}
                      </div>

                      <div
                        className="absolute"
                        style={{
                          bottom: "25%",
                          right: "15%",
                          fontSize: "16px",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          textAlign: "center",
                          zIndex: 1,
                        }}
                      >
                        ജനറൽ സെക്രട്ടറി
                        <br />
                        ഹാദിയ
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* -------- English Tab -------- */}
            <TabsContent value="english">
              {/* Mobile View */}
              <div className="block sm:hidden">
                <div className="flex flex-col items-center space-y-4">
                  <div className="text-center space-y-2">
                    <h3 className="font-semibold text-lg">
                      Sponsorship Letter
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Download Letter for {currSymbol}
                      {sponsorship.amountPromised.toLocaleString()} sponsorship
                    </p>
                  </div>
                  <div className="w-full max-w-sm space-y-4">
                    <div className="bg-muted p-4 rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Sponsor Name
                        </span>
                        <span className="text-sm font-medium">
                          {sponsorship.sponsorName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          Amount Promised
                        </span>
                        <span className="text-sm font-medium">
                          {currSymbol}
                          {sponsorship.amountPromised.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop View */}
              <div className="hidden sm:block">
                <div className="flex flex-col items-center space-y-4 w-full">
                  <div
                    className="relative w-[783px] h-[1000px]"
                    style={{ maxWidth: "100%", aspectRatio: "783/1000" }}
                  >
                    <div
                      ref={certificateRef}
                      className="absolute inset-0 bg-white"
                      style={{
                        width: "783px",
                        height: "1000px",
                        position: "relative",
                      }}
                    >
                      {posterImage && (
                        <img
                          src="/sponsor-2026.jpeg"
                          alt="Certificate background"
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}

                      <div
                        className="absolute text-gray-800"
                        style={{
                          top: "55%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "550px",
                          fontSize: "18px",
                          lineHeight: "1",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          zIndex: 1,
                        }}
                      >
                        <div>Assalamu Alaikum</div>
                        <br />
                        <div>
                          Dear{" "}
                          <span style={{ fontWeight: "bold" }}>
                            {sponsorship.sponsorName}
                          </span>
                          ,
                        </div>
                        <br />
                        <div
                          className="text-justify"
                          style={{ lineHeight: "1.6" }}
                        >
                          &nbsp;&nbsp;&nbsp;Thank you for your willingness to
                          contribute{" "}
                          <span style={{ fontWeight: "bold" }}>
                            {currSymbol} {sponsorship.amountPromised}/-
                          </span>{" "}
                          as part of the Hadia National Education Program.
                          <br />
                          &nbsp;&nbsp;&nbsp;After making the contribution,
                          please contact{" "}
                          <span style={{ fontWeight: "bold" }}>
                            +919744477555
                          </span>{" "}
                          to receive your receipt. May Allah reward you with the
                          deserved reward for this cooperation in the path of
                          goodness. Ameen.
                        </div>
                      </div>

                      <div
                        style={{
                          position: "absolute",
                          bottom: "100px",
                          left: "40px",
                          transform: "rotate(-90deg)",
                          transformOrigin: "left bottom",
                          fontSize: "12px",
                          color: "#666666",
                          fontFamily: "Arial",
                        }}
                      >
                        #{sponsorship.sponsorshipId}
                      </div>

                      <div
                        className="absolute"
                        style={{
                          bottom: "24.5%",
                          right: "18%",
                          fontSize: "16px",
                          fontFamily: "Apple Cards Afsal, sans-serif",
                          textAlign: "center",
                          zIndex: 1,
                        }}
                      >
                        General Secretary
                        <br />
                        HADIA
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <Button
            onClick={handleDownload}
            className="mt-4 w-full sm:w-auto"
            disabled={isLoading || !posterImage || !fontLoaded}
          >
            <Download className="mr-2 h-4 w-4" />
            {isLoading
              ? "Generating..."
              : !posterImage || !fontLoaded
                ? "Loading resources..."
                : "Download Letter"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

CertificateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sponsorship: PropTypes.shape({
    sponsorshipId: PropTypes.string.isRequired,
    sponsorName: PropTypes.string.isRequired,
    amountPromised: PropTypes.number.isRequired,
    currency: PropTypes.oneOf([
      "INR",
      "SAR",
      "QAR",
      "AED",
      "KWD",
      "OMR",
      "BHD",
      "MYR",
    ]),
  }).isRequired,
};
